import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search, Terminal, Activity, Database, Shield } from 'lucide-react';
import AppLayout from '../components/AppLayout';

const HomePage = () => {
    const navigate = useNavigate();
    const [localQuery, setLocalQuery] = useState('');

    useEffect(() => {
        document.title = "AltFinder // Access Terminal";
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (localQuery.trim()) {
            navigate(`/index?search=${encodeURIComponent(localQuery)}`);
        }
    };

    return (
        <AppLayout>
            <div className="h-full overflow-y-auto bg-[#0a0a0a] text-[#e5e5e5] font-sans selection:bg-[var(--fg-primary)] selection:text-[var(--bg-main)]">

                {/* Section 1: The Input Terminal */}
                <div className="min-h-[50vh] flex flex-col items-center justify-center p-8 border-b border-[var(--border-main)] relative">
                    <div className="absolute top-6 left-6 font-mono text-xs text-[var(--fg-tertiary)] uppercase tracking-widest flex items-center gap-2">
                        <span className="w-2 h-2 bg-[var(--signal-green)] rounded-full animate-pulse"></span>
                        Net_Status: Online
                    </div>

                    <div className="w-full max-w-2xl">
                        <div className="text-center mb-12">
                            <h1 className="font-serif text-5xl md:text-7xl mb-6 tracking-tight">
                                Index <span className="text-[var(--fg-tertiary)]">Zero.</span>
                            </h1>
                            <p className="font-mono text-xs md:text-sm text-[var(--fg-secondary)] uppercase tracking-widest">
                                The search engine for digital sovereignty.
                            </p>
                        </div>

                        <form onSubmit={handleSearch} className="relative group">
                            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                                <Search className="text-[var(--fg-tertiary)] group-focus-within:text-[var(--fg-primary)] transition-colors" size={20} />
                            </div>
                            <input
                                type="text"
                                value={localQuery}
                                onChange={(e) => setLocalQuery(e.target.value)}
                                placeholder="QUERY_PROTOCOL..."
                                className="w-full bg-[#111] border border-[var(--border-main)] text-lg font-mono py-6 pl-14 pr-32 focus:outline-none focus:border-[var(--fg-primary)] transition-colors text-[var(--fg-primary)] placeholder-[var(--fg-tertiary)]"
                                autoFocus
                            />
                            <button type="submit" className="absolute right-2 top-2 bottom-2 px-6 bg-[var(--fg-primary)] text-[var(--bg-main)] font-mono text-xs uppercase hover:bg-[var(--fg-secondary)] transition-colors hidden md:flex items-center">
                                ENTER <ArrowRight size={12} className="ml-2" />
                            </button>
                        </form>

                        <div className="flex justify-center gap-4 mt-8 font-mono text-[10px] text-[var(--fg-tertiary)] uppercase">
                            <span>Index Size: 45+ Nodes</span>
                            <span>//</span>
                            <span>Last Update: T-Minus 2h</span>
                        </div>
                    </div>
                </div>

                {/* Section 2: Data Stream */}
                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[var(--border-main)] border-b border-[var(--border-main)]">
                    <div className="p-12 hover:bg-[#111] transition-colors group cursor-pointer" onClick={() => navigate('/index?category=privacy')}>
                        <div className="mb-6 text-[var(--fg-tertiary)] group-hover:text-[var(--signal-green)] transition-colors">
                            <Shield size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-2xl mb-2 group-hover:translate-x-1 transition-transform">Privacy Sector</h3>
                        <p className="font-mono text-xs text-[var(--fg-secondary)] leading-relaxed">
                            Tools engineered for encryption, anonymity, and zero-knowledge data storage.
                        </p>
                    </div>
                    <div className="p-12 hover:bg-[#111] transition-colors group cursor-pointer" onClick={() => navigate('/index?license=opensource')}>
                        <div className="mb-6 text-[var(--fg-tertiary)] group-hover:text-[var(--signal-green)] transition-colors">
                            <Database size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-2xl mb-2 group-hover:translate-x-1 transition-transform">Open Archive</h3>
                        <p className="font-mono text-xs text-[var(--fg-secondary)] leading-relaxed">
                            Full repository of FOSS (Free and Open Source Software) solutions.
                        </p>
                    </div>
                    <div className="p-12 hover:bg-[#111] transition-colors group cursor-pointer" onClick={() => navigate('/domains')}>
                        <div className="mb-6 text-[var(--fg-tertiary)] group-hover:text-[var(--signal-green)] transition-colors">
                            <Activity size={32} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-2xl mb-2 group-hover:translate-x-1 transition-transform">Live Domains</h3>
                        <p className="font-mono text-xs text-[var(--fg-secondary)] leading-relaxed">
                            Real-time categorization of software across development, design, and productivity sectors.
                        </p>
                    </div>
                </div>

                {/* Section 3: System Log / Footer */}
                <div className="p-12 grid grid-cols-1 md:grid-cols-2 gap-12 bg-[#0a0a0a]">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <Terminal size={16} className="text-[var(--fg-tertiary)]" />
                            <span className="font-mono text-xs uppercase tracking-widest text-[var(--fg-tertiary)]">System_Log</span>
                        </div>
                        <div className="font-mono text-xs text-[var(--fg-secondary)] space-y-2">
                            <div className="flex gap-4">
                                <span className="text-[var(--fg-tertiary)]">14:02:11</span>
                                <span>New Node Identified: "Notesnook" [Privacy]</span>
                            </div>
                            <div className="flex gap-4">
                                <span className="text-[var(--fg-tertiary)]">13:45:00</span>
                                <span>Index Re-validation Complete.</span>
                            </div>
                            <div className="flex gap-4">
                                <span className="text-[var(--fg-tertiary)]">12:30:22</span>
                                <span>Protocol v2.0.4 Online.</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col justify-end items-end text-right">
                        <h2 className="font-serif text-3xl text-[var(--fg-primary)] mb-2">Join the Network.</h2>
                        <p className="font-mono text-xs text-[var(--fg-secondary)] mb-6 max-w-sm">
                            The index is community-maintained. Submit a pull request to add new nodes.
                        </p>
                        <button onClick={() => window.open('https://github.com', '_blank')} className="border-b border-[var(--fg-primary)] pb-1 font-mono text-xs uppercase hover:text-[var(--fg-tertiary)] transition-colors">
                            GitHub_Repository &rarr;
                        </button>
                    </div>
                </div>

            </div>
        </AppLayout>
    );
};

export default HomePage;
